import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check10, Info16 } from 'shared/assets/icons';
import { Icon } from 'shared/components/ui/icon';
import { Tooltip } from 'shared/components/ui/tooltip';

import * as Styled from './purchase-premium.style';

const uspTranslations = [
  { label: 'dialogs.premium.usp.1' },
  { label: 'dialogs.premium.usp.2' },
  { label: 'dialogs.premium.usp.3', tooltip: 'dialogs.premium.usp.3.tooltip' },
  { label: 'dialogs.premium.usp.4', tooltip: 'dialogs.premium.usp.4.tooltip' },
  { label: 'dialogs.premium.usp.5' },
  { label: 'dialogs.premium.usp.6', tooltip: 'dialogs.premium.usp.6.tooltip' },
  { label: 'dialogs.premium.usp.7', tooltip: 'dialogs.premium.usp.7.tooltip' },
  { label: 'dialogs.premium.usp.8' },
  { label: 'dialogs.premium.usp.9' },
];

export type UspListProps = {
  twoColumns?: boolean;
};

export const UspList: React.FC<UspListProps> = ({ twoColumns }) => {
  const [columnsToDisplay, setColumnsToDisplay] = useState(twoColumns ? 2 : 1);
  const widthRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const widthEl = widthRef.current;
    if (!widthEl || !twoColumns) {
      return;
    }

    const resizeObserver = new ResizeObserver((entries) =>
      entries.forEach((entry) =>
        setColumnsToDisplay(entry.contentRect.width >= 344 ? 2 : 1),
      ),
    );
    resizeObserver.observe(widthEl);

    setColumnsToDisplay(widthEl.clientWidth >= 344 ? 2 : 1);

    return () => resizeObserver.unobserve(widthEl);
  }, [twoColumns, widthRef]);

  return (
    <Styled.UspsContainer>
      <Styled.WidthRef ref={widthRef} />

      <Styled.UspList
        $noPadding={!!twoColumns}
        $twoColumns={columnsToDisplay === 2}
      >
        {uspTranslations.map(({ label, tooltip }) => (
          <Styled.UspListItem key={label}>
            <Usp label={label} tooltip={tooltip} />
          </Styled.UspListItem>
        ))}
      </Styled.UspList>
    </Styled.UspsContainer>
  );
};

type UspProps = {
  label: string;
  tooltip?: string;
};

const Usp: React.FC<UspProps> = ({ label, tooltip }) => {
  const tooltipRef = useRef<HTMLSpanElement>(null);
  const { t } = useTranslation();

  return (
    <>
      <Styled.UspListItemIcon>
        <Icon icon={Check10} />
      </Styled.UspListItemIcon>
      <Styled.UspTitleContainer>
        <Styled.UspTitle>{t(label)}</Styled.UspTitle>
        {!!tooltip && (
          <>
            <Styled.UspTooltipIconContainer ref={tooltipRef}>
              <Icon icon={Info16} />
            </Styled.UspTooltipIconContainer>
            <Tooltip label={t(tooltip)} referenceElement={tooltipRef} />
          </>
        )}
      </Styled.UspTitleContainer>
    </>
  );
};
